import classNames from '@/common/utils/classNames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Time picker as Nelson's «انتخاب زمان»: two wheels (hour : minute) in a bottom sheet, iOS-style, with a
 * ✕ and a ✓. Ported from nelson-front's VacationWheelTimePicker so a time is picked the same way everywhere.
 */
const ROW_H = 36;
const VISIBLE_ROWS = 5;
const SPACERS = (VISIBLE_ROWS - 1) / 2;
export const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
export const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const toFa = (value: string) => value.replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

export const Wheel = ({ items, value, onChange, label, format = toFa }: { items: string[]; value: string; onChange: (value: string) => void; label: string; format?: (item: string) => string }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const touching = useRef(false);
  const snapping = useRef(false);

  const scrollTo = useCallback(
    (item: string, smooth = false) => {
      const el = scroller.current;
      if (!el) return;
      el.scrollTo({ top: Math.max(0, items.indexOf(item)) * ROW_H, behavior: smooth ? 'smooth' : 'auto' });
    },
    [items],
  );

  // Open on the current value (once the sheet has its height).
  useEffect(() => {
    scrollTo(value);
    const raf = requestAnimationFrame(() => scrollTo(value));
    const later = setTimeout(() => scrollTo(value), 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(later);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settle = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const index = Math.min(items.length - 1, Math.max(0, Math.round(el.scrollTop / ROW_H)));
    snapping.current = true;
    scrollTo(items[index], true);
    if (items[index] !== value) onChange(items[index]);
    setTimeout(() => {
      snapping.current = false;
    }, 200);
  }, [items, onChange, scrollTo, value]);

  const settleSoon = (ms: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(settle, ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="relative flex-1" style={{ height: ROW_H * VISIBLE_ROWS }} aria-label={label}>
      <div className="pointer-events-none absolute inset-x-1 top-1/2 -translate-y-1/2 rounded-xl bg-[#78788014]" style={{ height: ROW_H }} aria-hidden />
      <div
        ref={scroller}
        role="listbox"
        aria-label={label}
        onTouchStart={() => {
          touching.current = true;
          clearTimeout(timer.current);
        }}
        onTouchEnd={() => {
          touching.current = false;
          settleSoon(180);
        }}
        onScroll={() => !touching.current && !snapping.current && settleSoon(120)}
        className="absolute inset-0 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{
          scrollSnapType: 'y mandatory',
          // snap points line up with the centered row (item i at scrollTop i * ROW_H), not with the top edge
          scrollPaddingTop: SPACERS * ROW_H,
          touchAction: 'pan-y',
          maskImage: 'linear-gradient(to bottom, transparent, #000 30%, #000 70%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000 30%, #000 70%, transparent)',
        }}
      >
        {Array.from({ length: SPACERS }, (_, i) => (
          <div key={`t${i}`} style={{ height: ROW_H }} aria-hidden />
        ))}
        {items.map(item => (
          <div
            key={item}
            role="option"
            aria-selected={item === value}
            style={{ height: ROW_H, scrollSnapAlign: 'start' }}
            className={classNames('flex items-center justify-center text-[17px] leading-none', item === value ? 'font-semibold text-[#1d1d1f]' : 'font-medium text-[#8e8e93]')}
          >
            {format(item)}
          </div>
        ))}
        {Array.from({ length: SPACERS }, (_, i) => (
          <div key={`b${i}`} style={{ height: ROW_H }} aria-hidden />
        ))}
      </div>
    </div>
  );
};

export const TimeWheelSheet = ({
  open,
  title,
  value,
  onChange,
  onClose,
}: {
  open: boolean;
  title: string;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}) => {
  const [hour, setHour] = useState('00');
  const [minute, setMinute] = useState('00');

  useEffect(() => {
    if (!open) return;
    const [h = '00', m = '00'] = value.split(':');
    setHour(h.padStart(2, '0'));
    setMinute(m.padStart(2, '0'));
    // Re-read the value only when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  const choose = (h: string, m: string) => {
    setHour(h);
    setMinute(m);
    onChange(`${h}:${m}`);
  };

  return createPortal(
    <div className="fixed inset-0 z-[2147483000] flex flex-col justify-end" role="presentation">
      <button type="button" aria-label="بستن" onClick={onClose} className="absolute inset-0 animate-[wheelFade_200ms_ease-out] bg-black/35" />
      <div
        role="dialog"
        aria-label={title}
        className="relative animate-[wheelUp_280ms_ease-out] rounded-t-[20px] bg-white pb-[max(8px,env(safe-area-inset-bottom))] shadow-[0_-8px_32px_rgba(0,0,0,0.15)]"
      >
        <div className="grid grid-cols-[32px_1fr_32px] items-center gap-2 border-b border-[#ebebef] px-3.5 py-2.5" dir="ltr">
          <button type="button" aria-label="بستن" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f2f2f7] text-[#86868b] active:scale-95">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
          <span className="text-center text-[15px] font-semibold text-[#1d1d1f]" dir="rtl">
            {title}
          </span>
          <button type="button" aria-label="تأیید" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white active:scale-95">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12.5L9.5 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 px-4 pb-3 pt-2" dir="ltr">
          <Wheel items={HOURS} value={hour} label="ساعت" onChange={h => choose(h, minute)} />
          <span className="pb-0.5 text-[17px] font-semibold text-[#1d1d1f]">:</span>
          <Wheel items={MINUTES} value={minute} label="دقیقه" onChange={m => choose(hour, m)} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TimeWheelSheet;
